import { useCallback, useRef, useState } from "react";
import { UploadCloud, X, Loader2, Image as ImageIcon } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Cover image dropzone with preview + upload via Emergent Object Storage.
 * Props:
 *  - value: current URL (or empty)
 *  - onChange: (url) => void  — pass "" to clear
 *  - testid: root data-testid
 */
export default function CoverImageDropzone({ value, onChange, testid = "cover-dropzone" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = useCallback(async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Seules les images sont acceptées");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.url) onChange?.(res.data.url);
      toast.success("Image de couverture téléchargée");
    } catch (e) {
      toast.error("Upload échoué : " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  return (
    <div data-testid={testid}>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} data-testid={`${testid}-input`} />
      {value ? (
        <div className="relative group rounded-lg overflow-hidden border border-white/10 bg-black">
          <img src={value} alt="Cover" className="w-full aspect-video object-cover" />
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="px-3 py-1.5 rounded-md bg-emerald-500 text-black text-xs font-semibold hover:bg-emerald-300 flex items-center gap-1.5"
              data-testid={`${testid}-replace`}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
              Remplacer
            </button>
            <button
              type="button"
              onClick={() => onChange?.("")}
              disabled={uploading}
              className="px-3 py-1.5 rounded-md bg-white/10 text-white text-xs font-semibold hover:bg-rose-500/20 hover:text-rose-300 flex items-center gap-1.5"
              data-testid={`${testid}-remove`}
            >
              <X className="w-3.5 h-3.5" /> Retirer
            </button>
          </div>
        </div>
      ) : (
        <div
          role="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`aspect-video rounded-lg border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer ${
            dragOver
              ? "border-emerald-400 bg-emerald-500/5 text-emerald-300"
              : "border-white/10 bg-white/[0.02] text-zinc-500 hover:border-white/20 hover:text-zinc-300"
          }`}
          data-testid={`${testid}-drop`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <div className="text-xs">Téléchargement…</div>
            </>
          ) : (
            <>
              <ImageIcon className="w-6 h-6" />
              <div className="text-sm font-medium">Image de couverture</div>
              <div className="text-[11px] text-zinc-500">Glisser-déposer ou cliquer pour choisir un fichier (PNG · JPG · WEBP · GIF)</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
